import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';

const STREAM_REFRESH_INTERVAL_MS = 15000; // Same as dashboard-refresh on viewer page
import phenix from '@phenixrts/sdk';
import { PhenixChannelProvider } from './PhenixChannelContext';
import PhenixVideoPlayer from './PhenixVideoPlayer';
import ChannelHeader from './ChannelHeader';
import StreamSlotPlaceholder from './StreamSlotPlaceholder';
import { getTotalSlots, getChannelPlacements } from '../utils/gridUtils';
import { getHABorderColor, getStreamLabel } from '../utils/streamUtils';
import { isChannelOffline } from '../utils/channelStateUtils';

const DropTargetOverlay = () => (
  <div className="absolute inset-0 z-30 flex items-center justify-center bg-blue-500/30 border-2 border-blue-400 border-dashed rounded pointer-events-none">
    <div className="flex flex-col items-center gap-1">
      <svg className="w-10 h-10 text-white drop-shadow-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="7 16 12 21 17 16" />
        <polyline points="7 8 12 3 17 8" />
        <line x1="12" y1="21" x2="12" y2="3" />
      </svg>
      <span className="text-white text-xs font-semibold drop-shadow-lg">Drop to swap</span>
    </div>
  </div>
);

const PreviewArea = ({ selectedChannels, applicationId, applicationSecret, onChannelsReorder }) => {
  const [gridLayout, setGridLayout] = useState({ rows: 1, cols: 1 });
  const [containerRef, setContainerRef] = useState(null);
  const [channelTokens, setChannelTokens] = useState(new Map());
  const [draggedChannel, setDraggedChannel] = useState(null);
  const [dragOverChannel, setDragOverChannel] = useState(null);
  const [hintDismissed, setHintDismissed] = useState(() =>
    localStorage.getItem('previewDragHintDismissed') === '1'
  );
  const showDragHint = !hintDismissed && selectedChannels.length >= 2;

  const dismissHint = () => {
    setHintDismissed(true);
    localStorage.setItem('previewDragHintDismissed', '1');
  };

  // Calculate optimal grid layout based on container size and slot count.
  // Iterates all possible row counts and picks the one maximizing cell area,
  // matching VideoGrid's approach so preview and dashboard column counts agree.
  const calculateLayout = useCallback(() => {
    if (!selectedChannels.length) return;
    const totalSlots = getTotalSlots(selectedChannels);
    const hasHA = selectedChannels.some(ch => ch.expectedStreams === 2);
    const aspectRatio = 16 / 9;
    const gap = 8;

    if (!containerRef) {
      // Fallback before container mounts: simple sqrt
      const cols = Math.ceil(Math.sqrt(totalSlots));
      const rows = Math.ceil(totalSlots / cols);
      const dimensions = { rows, cols };
      const placements = getChannelPlacements(selectedChannels, dimensions);
      setGridLayout({ ...dimensions, totalSlots, placements });
      return;
    }

    const availableWidth = containerRef.offsetWidth - 16; // p-2 = 8px each side
    const availableHeight = containerRef.offsetHeight - 16;

    let bestLayout = { rows: 1, cols: totalSlots };
    let maxArea = 0;

    for (let rows = 1; rows <= totalSlots; rows++) {
      let cols = Math.ceil(totalSlots / rows);
      if (hasHA && cols % 2 !== 0) cols += 1;

      const widthForCells = availableWidth - (gap * (cols - 1));
      const heightForCells = availableHeight - (gap * (rows - 1));
      const maxCellWidth = Math.floor(widthForCells / cols);
      const maxCellHeight = Math.floor(heightForCells / rows);

      let cellWidth, cellHeight;
      if (maxCellWidth / maxCellHeight > aspectRatio) {
        cellHeight = maxCellHeight;
        cellWidth = Math.floor(cellHeight * aspectRatio);
      } else {
        cellWidth = maxCellWidth;
        cellHeight = Math.floor(cellWidth / aspectRatio);
      }

      const area = cellWidth * cellHeight;
      if (area > maxArea) {
        maxArea = area;
        bestLayout = { rows, cols };
      }
    }

    const placements = getChannelPlacements(selectedChannels, bestLayout);
    setGridLayout({ ...bestLayout, totalSlots, placements });
  }, [selectedChannels, containerRef]);

  useEffect(() => {
    calculateLayout();
  }, [calculateLayout]);

  useEffect(() => {
    if (!containerRef) return;
    const handleResize = () => calculateLayout();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef, calculateLayout]);

  const generateChannelToken = useCallback(async (channelId) => {
    try {
      const authToken = btoa(`${applicationId}:${applicationSecret}`);
      const response = await fetch('/api/generate-channel-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${authToken}`
        },
        body: JSON.stringify({
          channelId,
          expiresInSeconds: 24 * 60 * 60
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate channel token');
      }

      const { token } = await response.json();
      return token;
    } catch (error) {
      console.error(`Token generation error for channel ${channelId}:`, error);
      throw error;
    }
  }, [applicationId, applicationSecret]);

  const getPlayerDimensions = useCallback(() => {
    if (!containerRef) return { width: '100%', height: '100%' };

    const containerWidth = containerRef.offsetWidth;
    const containerHeight = containerRef.offsetHeight;
    const aspectRatio = 16 / 9;

    const width = Math.floor(containerWidth / gridLayout.cols);
    const height = Math.min(
      width / aspectRatio,
      containerHeight / gridLayout.rows
    );

    return {
      width: `${width}px`,
      height: `${height}px`
    };
  }, [containerRef, gridLayout]);

  const getChannelToken = useCallback(async (channelId) => {
    if (!channelTokens.has(channelId)) {
      const token = await generateChannelToken(channelId);
      setChannelTokens(prev => new Map(prev).set(channelId, token));
      return token;
    }
    return channelTokens.get(channelId);
  }, [generateChannelToken, channelTokens]);

  // For HA: fetch stream-specific tokens + streamLabels; fall back to channel token when not enough streams
  const streamTokensCache = useRef(new Map());
  const fetchStreamTokens = useCallback(async (channelId, { forceRefresh = false } = {}) => {
    if (!forceRefresh && streamTokensCache.current.has(channelId)) {
      return streamTokensCache.current.get(channelId);
    }
    if (forceRefresh) {
      streamTokensCache.current.delete(channelId);
    }
    try {
      const authToken = btoa(`${applicationId}:${applicationSecret}`);
      const response = await fetch('/api/stream-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${authToken}`
        },
        body: JSON.stringify({ channelId })
      });
      if (!response.ok) throw new Error('Failed to fetch stream tokens');
      const { streamIds, streamTokens, streamLabels } = await response.json();
      const cached = {
        streamIds: streamIds || [],
        streamTokens: streamTokens || {},
        streamLabels: streamLabels || {}
      };
      streamTokensCache.current.set(channelId, cached);
      return cached;
    } catch (err) {
      console.error(`Stream tokens for ${channelId}:`, err);
      return { streamIds: [], streamTokens: {}, streamLabels: {} };
    }
  }, [applicationId, applicationSecret]);

  const getTokenForSlot = useCallback(async (channelId, slotIndex, isHA) => {
    if (!isHA) {
      return getChannelToken(channelId);
    }
    const { streamIds, streamTokens } = await fetchStreamTokens(channelId);
    const streamId = streamIds[slotIndex];
    if (streamId && streamTokens[streamId]) {
      return streamTokens[streamId];
    }
    return getChannelToken(channelId);
  }, [getChannelToken, fetchStreamTokens]);

  // Pre-fetch channel tokens for Stats/Portal links (including HA channels)
  useEffect(() => {
    selectedChannels.forEach(ch => {
      getChannelToken(ch.channelId).catch(() => {});
    });
  }, [selectedChannels, getChannelToken]);

  // Use a ref-based timeout to keep drag-over state alive.
  // onDragOver fires continuously (~every 50ms) while the cursor is over an element,
  // so we reset a short timeout on each fire. When the cursor leaves, onDragOver stops
  // firing and the timeout clears the state. This is immune to the relatedTarget=null
  // problem that plagues onDragEnter/onDragLeave with video elements and iframes.
  const dragOverTimeout = useRef(null);

  const handleDragStart = (e, channel) => {
    setDraggedChannel(channel);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedChannel(null);
    setDragOverChannel(null);
    clearTimeout(dragOverTimeout.current);
  };

  const handleDragOver = (e, channel) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (channel && draggedChannel && channel.channelId !== draggedChannel.channelId) {
      setDragOverChannel(channel.channelId);
      clearTimeout(dragOverTimeout.current);
      dragOverTimeout.current = setTimeout(() => setDragOverChannel(null), 150);
    }
  };

  const handleDrop = (e, targetChannel) => {
    e.preventDefault();
    setDragOverChannel(null);
    clearTimeout(dragOverTimeout.current);
    if (!draggedChannel || targetChannel.channelId === draggedChannel.channelId) return;

    const newChannels = [...selectedChannels];
    const draggedIdx = newChannels.findIndex(ch => ch.channelId === draggedChannel.channelId);
    const targetIdx = newChannels.findIndex(ch => ch.channelId === targetChannel.channelId);

    newChannels.splice(draggedIdx, 1);
    newChannels.splice(targetIdx, 0, draggedChannel);

    onChannelsReorder(newChannels);
    if (!hintDismissed) dismissHint();
  };

  if (!selectedChannels.length) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500">
        Select channels to preview
      </div>
    );
  }

  const dims = getPlayerDimensions();
  const cellWidth = dims.width ? parseInt(dims.width, 10) : 320;
  const cellHeight = dims.height ? parseInt(dims.height, 10) : 180;
  const dimensions = { rows: gridLayout.rows, cols: gridLayout.cols };
  const placements = gridLayout.placements ?? getChannelPlacements(selectedChannels, dimensions);

  return (
    <div className="w-full h-full bg-gray-900 flex flex-col overflow-hidden">
      {showDragHint && (
        <div className="flex-shrink-0 px-3 py-1.5 bg-gray-800 text-gray-300 text-xs flex items-center justify-between">
          <span>Drag channels to reorder their position in the dashboard</span>
          <button
            onClick={dismissHint}
            className="ml-4 text-gray-500 hover:text-gray-300 font-bold text-sm leading-none"
            aria-label="Dismiss hint"
          >
            &times;
          </button>
        </div>
      )}
      <div
        ref={setContainerRef}
        className="flex-1 p-2 min-h-0"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)`,
          gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
          gap: '8px',
          alignItems: 'stretch',
          justifyItems: 'stretch'
        }}
      >
      {selectedChannels.map((channel, idx) => {
        const isHA = channel.expectedStreams === 2;
        const placement = placements[idx] || { gridRow: 1, gridColumn: 1, gridColumnSpan: isHA ? 2 : 1 };
        const gridStyle = {
          gridRow: placement.gridRow,
          gridColumn: `${placement.gridColumn} / span ${placement.gridColumnSpan}`,
          width: '100%',
          minWidth: 0
        };
        if (isHA) {
          return (
            <PreviewHAChannel
              key={channel.channelId}
              channel={channel}
              index={idx}
              getChannelToken={getChannelToken}
              getTokenForSlot={getTokenForSlot}
              fetchStreamTokens={fetchStreamTokens}
              cellWidth={cellWidth}
              cellHeight={cellHeight}
              gridStyle={gridStyle}
              isDragSource={draggedChannel?.channelId === channel.channelId}
              isDragOver={dragOverChannel === channel.channelId}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          );
        }
        return (
          <PreviewSingleChannel
            key={channel.channelId}
            channel={channel}
            index={idx}
            getChannelToken={getChannelToken}
            getTokenForSlot={getTokenForSlot}
            phenix={phenix}
            cellWidth={cellWidth}
            cellHeight={cellHeight}
            gridStyle={gridStyle}
            isDragSource={draggedChannel?.channelId === channel.channelId}
            isDragOver={dragOverChannel === channel.channelId}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />
        );
      })}
      </div>
    </div>
  );
};

const PreviewHAChannel = ({
  channel,
  index,
  getChannelToken,
  getTokenForSlot,
  fetchStreamTokens,
  cellWidth,
  cellHeight,
  gridStyle,
  isDragSource,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}) => {
  const [streamData, setStreamData] = useState(null);
  const { channelId, alias } = channel;

  const streamIdsSetEqual = useCallback((a, b) => {
    const sa = new Set(a || []);
    const sb = new Set(b || []);
    if (sa.size !== sb.size) return false;
    for (const id of sa) if (!sb.has(id)) return false;
    return true;
  }, []);

  useEffect(() => {
    fetchStreamTokens(channelId).then(setStreamData);
  }, [channelId, fetchStreamTokens]);

  // Periodic refresh of member/stream state (same cadence as dashboard-refresh).
  // Only update when stream set changes — skip when order-only differs (same API behavior as dashboard).
  useEffect(() => {
    const refresh = () => {
      fetchStreamTokens(channelId, { forceRefresh: true }).then((cached) => {
        setStreamData((prev) => {
          if (!prev) return cached;
          if (streamIdsSetEqual(prev.streamIds, cached.streamIds)) return prev;
          return cached;
        });
      });
    };
    const intervalId = setInterval(refresh, STREAM_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [channelId, fetchStreamTokens, streamIdsSetEqual]);

  const streamIds = streamData?.streamIds || [];
  const streamLabels = streamData?.streamLabels || {};
  const tokensByStream = streamData?.streamTokens || {};
  // Sort for stable slot assignment when API returns different order across polls.
  const displayStreamIds = [...streamIds].sort().slice(0, 2);
  const memberCount = displayStreamIds.length;
  const expectedStreams = channel.expectedStreams ?? 2;
  const isDegraded = expectedStreams === 2 && memberCount < 2;
  const isDown = memberCount === 0;
  const borderColor = getHABorderColor(isDown, isDegraded);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, channel)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, channel)}
      onDrop={(e) => onDrop(e, channel)}
      className={`relative rounded overflow-hidden border-2 ${borderColor} bg-black flex flex-col cursor-move transition-opacity ${isDragSource ? 'opacity-40' : ''}`}
      style={{ ...gridStyle, height: '100%', minHeight: cellHeight }}
    >
      {isDragOver && <DropTargetOverlay />}
      <span className="absolute bottom-1 right-1 z-20 bg-black/60 text-white text-xs font-mono rounded px-1">{index + 1}</span>
      <PreviewChannelHeader channel={channel} isHA expectedStreams={expectedStreams} memberCount={memberCount} />
      <div className="flex-1 flex gap-2 p-2 min-h-0">
        {[0, 1].map((idx) => {
          const streamId = displayStreamIds[idx];
          const streamToken = streamId ? tokensByStream[streamId] : null;
          const contextKey = streamId ? `${channelId}-${streamId}` : `${channelId}-placeholder-${idx}`;
          const hasStream = !!streamToken;

          if (!hasStream) {
            const message = memberCount === 0 ? (
              'Channel offline'
            ) : (
              <>
                <div className="font-medium text-amber-500">Stream missing</div>
                <div className="text-xs mt-1">
                  Expected 2 streams, only {memberCount} active
                </div>
              </>
            );
            return (
              <StreamSlotPlaceholder
                key={contextKey}
                streamLabels={streamLabels}
                streamId={streamId}
                idx={idx}
                message={message}
              />
            );
          }

          return (
            <div
              key={streamId}
              className="flex-1 min-w-0 relative bg-black rounded overflow-hidden flex flex-col"
            >
              <div
                className="flex-shrink-0 px-2 py-1 bg-black bg-opacity-60 text-gray-300 text-xs truncate"
                title={streamLabels[streamId] || streamId}
              >
                {getStreamLabel(streamLabels, streamId, idx)}
              </div>
              <div className="flex-1 min-h-0">
                <PhenixChannelProvider channelId={contextKey}>
                  <Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-gray-800 text-white text-sm">Loading...</div>}>
                    <ChannelPlayer
                      channelId={channelId}
                      slotIndex={idx}
                      isHA={true}
                      getTokenForSlot={getTokenForSlot}
                      phenix={phenix}
                    />
                  </Suspense>
                </PhenixChannelProvider>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PreviewChannelHeader = ({ channel, isHA, expectedStreams, memberCount }) => {
  const { channelId, alias } = channel;

  return (
    <ChannelHeader
      alias={alias}
      channelId={channelId}
      isHA={isHA}
      expectedStreams={expectedStreams}
      memberCount={memberCount}
      showGrip
      className="cursor-move"
      onLinkClick={(e) => e.stopPropagation()}
    />
  );
};

const PreviewSingleChannel = ({
  channel,
  index,
  getChannelToken,
  getTokenForSlot,
  phenix,
  cellWidth,
  cellHeight,
  gridStyle,
  isDragSource,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}) => {
  const [isOffline, setIsOffline] = useState(false);
  const expectedStreams = channel.expectedStreams ?? 1;
  const borderColor = isOffline
    ? getHABorderColor(true, false, { singleChannelOffline: true })
    : 'border-gray-700';

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, channel)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, channel)}
      onDrop={(e) => onDrop(e, channel)}
      className={`relative rounded overflow-hidden border-2 ${borderColor} bg-black flex flex-col cursor-move transition-opacity ${isDragSource ? 'opacity-40' : ''}`}
      style={{ ...gridStyle, height: '100%', minHeight: cellHeight }}
    >
      {isDragOver && <DropTargetOverlay />}
      <span className="absolute bottom-1 right-1 z-20 bg-black/60 text-white text-xs font-mono rounded px-1">{index + 1}</span>
      <div className="absolute top-0 left-0 right-0 z-10">
        <PreviewChannelHeader
          channel={channel}
          isHA={false}
          expectedStreams={expectedStreams}
          memberCount={undefined}
        />
      </div>
      <div className="flex-1 flex min-h-0 relative">
        <PhenixChannelProvider channelId={channel.channelId}>
          <Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">Loading...</div>}>
            <ChannelPlayer
              channelId={channel.channelId}
              slotIndex={0}
              isHA={false}
              getTokenForSlot={getTokenForSlot}
              phenix={phenix}
              onStateChange={(state) => setIsOffline(isChannelOffline(state))}
            />
          </Suspense>
        </PhenixChannelProvider>
        {isOffline && (
          <div className="absolute inset-0 z-10 flex flex-col bg-gray-900 p-2">
            <StreamSlotPlaceholder
              streamLabels={{}}
              streamId={null}
              idx={0}
              message="Channel offline"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ChannelPlayer = ({ channelId, slotIndex, isHA, getTokenForSlot, phenix, onStateChange }) => {
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getTokenForSlot(channelId, slotIndex, isHA)
      .then(setToken)
      .catch(err => {
        console.error(`Failed to get token for channel ${channelId} slot ${slotIndex}:`, err);
        setError(err.message);
      });
  }, [channelId, slotIndex, isHA, getTokenForSlot]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-red-900 text-white p-4 text-sm">
        Failed to load channel: {error}
      </div>
    );
  }

  if (!token) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
        Initializing...
      </div>
    );
  }

  return <PhenixVideoPlayer phenix={phenix} token={token} onStateChange={onStateChange} />;
};

export default PreviewArea;
